import type {
  DesignBody,
  EditAreaDefinition,
  FaceKey,
  ResolvedEditAreaDefinition,
  Surface,
} from '@repo/api-contracts';
import {
  type CylinderParams,
  composeFace,
  createRaster,
  photoPercentRectToPx,
  type Raster,
  warpCylinder,
} from '@repo/warp-engine';
import { compose, scale, translate } from '../geometry/matrix';
import type { FaceMaterials, RenderMaterials } from '../materials';
import { compositeOver } from '../raster/composite';
import { fitWithinPixels, resizeRaster } from '../raster/resize-raster';
import { drawRasterTransformed } from '../raster/transform-raster';
import { cylinderParamsOf, toEnginePhoto } from '../surface/cylinder-params';
import { flatScale, surfaceMmToPhoto } from '../surface/flat-mapping';
import { wrappedPlacementSpans } from '../surface/wrap-placement';
import type { CreateContext, MeasureText, PhotoDimensions } from '../types';
import { drawMeshPlacement } from './draw-mesh-placement';
import { renderComponent } from './render-component';

export const DEFAULT_MAX_PREVIEW_PIXELS = 4_000_000;

export interface FacePreviewInput {
  readonly definition: EditAreaDefinition | ResolvedEditAreaDefinition;
  readonly colorOptionId: string;
  readonly faceKey: FaceKey;
  readonly body: DesignBody;
  readonly materials: RenderMaterials;
  readonly face: FaceMaterials;
  readonly createContext: CreateContext;
  readonly measure: MeasureText;
  /** 사진 픽셀 수 상한(T8). 넘으면 사진·마스크·음영을 같은 비율로 줄여 렌더한다. */
  readonly maxPixels?: number;
}

export interface FacePreviewResult {
  readonly raster: Raster;
  /** 결과 px ÷ 정의의 사진 px(colors[].faces.<면>.photoSize). */
  readonly scale: number;
}

type FlatSurface = Extract<Surface, { type: 'flat' }>;
type CylinderSurface = Extract<Surface, { type: 'cylinder' }>;

const fitRaster = (
  raster: Raster | undefined,
  width: number,
  height: number,
): Raster | undefined =>
  raster === undefined || (raster.width === width && raster.height === height)
    ? raster
    : resizeRaster(raster, width, height);

/** 어댑터 area 의 소수 크기를 정수 원본 크기로 맞춘 엔진 입력(어드민 renderCylinderPreview 와 같은 방식). */
const integerSourceParams = (
  params: CylinderParams,
): { params: CylinderParams; scaleX: number; scaleY: number } => {
  const width = Math.max(1, Math.round(params.area.width));
  const height = Math.max(1, Math.round(params.area.height));
  const scaleX = width / params.area.width;
  const scaleY = height / params.area.height;
  return {
    params: {
      area: { left: params.area.left * scaleX, top: params.area.top * scaleY, width, height },
      upSide: params.upSide * scaleY,
      downSide: params.downSide * scaleY,
      degree: params.degree,
    },
    scaleX,
    scaleY,
  };
};

const drawFlatPlacement = (
  layer: Raster,
  surface: FlatSurface,
  photo: PhotoDimensions,
  input: FacePreviewInput,
  component: { widthMm: number; heightMm: number },
  layers: DesignBody['components'][number]['layers'],
  position: { xMm: number; yMm: number },
): void => {
  const s = flatScale(surface, photo);
  const pxPerMm = Math.max(s.x, s.y);
  const comp = renderComponent({
    component,
    layers,
    materials: input.materials,
    pxPerMm,
    createContext: input.createContext,
    measure: input.measure,
  });
  const origin = surfaceMmToPhoto(surface, photo, position);
  const [tl, , br] = surface.points;
  const clip = photoPercentRectToPx(
    { left: tl.x, top: tl.y, width: br.x - tl.x, height: br.y - tl.y },
    toEnginePhoto(photo),
  );
  drawRasterTransformed(
    layer,
    comp,
    compose(translate(origin.x, origin.y), scale(s.x / pxPerMm, s.y / pxPerMm)),
    {
      x: clip.left,
      y: clip.top,
      width: clip.width,
      height: clip.height,
    },
  );
};

const drawCylinderPlacement = (
  layer: Raster,
  surface: CylinderSurface,
  photo: PhotoDimensions,
  input: FacePreviewInput,
  component: { widthMm: number; heightMm: number },
  layers: DesignBody['components'][number]['layers'],
  position: { xMm: number; yMm: number },
): void => {
  const { params, scaleX, scaleY } = integerSourceParams(cylinderParamsOf(surface, photo));
  const frame = surface.realSize;
  const sx = params.area.width / frame.widthMm;
  const sy = params.area.height / frame.heightMm;
  const pxPerMm = Math.max(sx, sy);
  const comp = renderComponent({
    component,
    layers,
    materials: input.materials,
    pxPerMm,
    createContext: input.createContext,
    measure: input.measure,
  });
  const source = createRaster(params.area.width, params.area.height);
  for (const span of wrappedPlacementSpans(position.xMm, component.widthMm, frame.widthMm)) {
    drawRasterTransformed(
      source,
      comp,
      compose(translate(span.originMm * sx, position.yMm * sy), scale(sx / pxPerMm, sy / pxPerMm)),
      {
        x: span.fromMm * sx,
        y: 0,
        width: (span.toMm - span.fromMm) * sx,
        height: params.area.height,
      },
    );
  }
  const preview = warpCylinder(source, params);
  compositeOver(
    layer,
    preview.raster,
    Math.floor(preview.placement.left / scaleX),
    Math.floor(preview.placement.top / scaleY),
  );
};

/**
 * 면 미리보기(사진 크기 래스터)를 만든다. 배치 순서대로(D13) flat 은 평행 배치, cylinder 는 감김(Q6) 뒤 워프, mesh 는 칸별 삼각형 아핀으로 휘고 표면 윤곽으로 자른다.
 * 면 합성 설정이 있으면 composeFace(마스크·음영·multiply, D6·D8), 없으면 사진 위 source-over. 안내선은 넣지 않는다(D14).
 */
export const renderFacePreview = (input: FacePreviewInput): FacePreviewResult => {
  const { definition, colorOptionId, faceKey, body, face } = input;
  const color = definition.colors.find((entry) => entry.colorOptionId === colorOptionId);
  const photoEntry = color?.faces[faceKey];
  if (photoEntry === undefined)
    throw new RangeError(`색상 ${colorOptionId} 에 ${faceKey} 면 사진이 없다`);
  const fit = fitWithinPixels(
    face.photo.width,
    face.photo.height,
    input.maxPixels ?? DEFAULT_MAX_PREVIEW_PIXELS,
  );
  const photo = fitRaster(face.photo, fit.width, fit.height) ?? face.photo;
  const mask = fitRaster(face.mask, fit.width, fit.height);
  const shading = fitRaster(face.shading, fit.width, fit.height);
  const photoDims: PhotoDimensions = { widthPx: photo.width, heightPx: photo.height };
  const faceDefinition = definition.faces[faceKey];
  const layer = createRaster(photo.width, photo.height);

  if (faceDefinition !== undefined) {
    for (const placement of faceDefinition.placements) {
      const surface = faceDefinition.surfaces.find(
        (candidate) => candidate.id === placement.surfaceId,
      );
      const component = definition.components.find(
        (candidate) => candidate.id === placement.componentId,
      );
      const layers =
        body.components.find((candidate) => candidate.componentId === placement.componentId)
          ?.layers ?? [];
      if (surface === undefined || component === undefined || layers.length === 0) continue;
      if (surface.type === 'flat')
        drawFlatPlacement(layer, surface, photoDims, input, component, layers, placement.position);
      else if (surface.type === 'cylinder')
        drawCylinderPlacement(
          layer,
          surface,
          photoDims,
          input,
          component,
          layers,
          placement.position,
        );
      else if (surface.type === 'mesh')
        drawMeshPlacement(layer, surface, photoDims, input, component, layers, placement.position);
    }
  }

  const composite = faceDefinition?.composite;
  const raster = composeFace({
    photo,
    preview: {
      raster: layer,
      placement: { left: 0, top: 0, width: photo.width, height: photo.height },
    },
    multiply: composite?.multiply ?? false,
    ...(mask === undefined ? {} : { mask }),
    ...(shading === undefined ? {} : { shading }),
  });
  return { raster, scale: photo.width / photoEntry.photoSize.widthPx };
};
